import { useEffect, useState } from 'react';
import type { TrackState } from '../../api.js';
import { statusForTrackState } from './statusMap.js';

interface TrackEvent {
  from_state: TrackState | null;
  to_state: TrackState;
  reason: string | null;
  created_at: string;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function ThemedTimeline({ trackId }: { trackId: string }) {
  const [events, setEvents] = useState<TrackEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`/api/tracks/${trackId}/events`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `request failed: ${res.status}`);
        }
        const body = (await res.json()) as { events: TrackEvent[] };
        if (!cancelled) {
          setEvents(body.events);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [trackId]);

  if (error) {
    return <div className="themed-timeline-empty themed-timeline-error">{error}</div>;
  }

  if (events === null) {
    return <div className="themed-timeline-empty">Loading…</div>;
  }

  if (events.length === 0) {
    return <div className="themed-timeline-empty">No events yet.</div>;
  }

  return (
    <ol className="themed-timeline">
      {events.map((ev, i) => {
        const { status } = statusForTrackState(ev.to_state);
        return (
          <li key={i} className="themed-timeline-row">
            <span className="build-dot" data-status={status} />
            <div className="themed-timeline-main">
              <div className="themed-timeline-state">
                {ev.from_state ? `${ev.from_state} → ${ev.to_state}` : ev.to_state}
              </div>
              {ev.reason && <div className="themed-timeline-reason">{ev.reason}</div>}
              <div className="themed-timeline-time">{relativeTime(ev.created_at)}</div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
